import React, { useState } from 'react';
import { ArrowLeft, Users, Wallet, Handshake, Zap, Sparkles, Shield, DollarSign, AlertTriangle } from 'lucide-react';
import CandidatoAvatar from '../../components/CandidatoAvatar';
import { capitalize } from './constants';

export default function VereadorSelector({ cityDetail, selectedCity, playerAvatar, onSelect, onBack }) {
  const [selected, setSelected] = useState(null);
  const vereadores = cityDetail?.politicos?.eleitos?.filter(e => e.cargo === 'VEREADOR') || [];

  const calcAge = (dt) => {
    if (!dt) return null;
    const parts = dt.split('/');
    if (parts.length < 3) return null;
    const birth = new Date(parts[2], parts[1] - 1, parts[0]);
    return Math.floor((Date.now() - birth) / 31557600000);
  };

  const sel = selected !== null ? vereadores[selected] : null;

  return (
    <div className="vereador-selector">
      <div className="vereador-selector__header">
        <div className="vereador-selector__header-row">
          {playerAvatar && (
            <div className="vereador-selector__avatar" dangerouslySetInnerHTML={{ __html: playerAvatar.svg }} />
          )}
          <div>
            <h2 className="vereador-selector__title">Escolha seu Vereador</h2>
            <p className="vereador-selector__desc">Você trabalhará como assessor deste vereador em {selectedCity?.name}</p>
          </div>
        </div>
      </div>

      <div className="vereador-selector__body">
        {/* Vereadores grid */}
        <div className="vereador-grid-wrap">
          <div className="vereador-grid">
            {vereadores.map((v, i) => {
              const isSelected = selected === i;
              const age = calcAge(v.dataNascimento);
              return (
                <button key={i} onClick={() => setSelected(i)}
                  className="vereador-card"
                  style={{
                    background: isSelected ? 'rgba(212,165,116,.08)' : 'rgba(255,255,255,.02)',
                    border: isSelected ? '2px solid rgba(212,165,116,.6)' : '1px solid rgba(255,255,255,.06)',
                    boxShadow: isSelected ? '0 0 0 3px rgba(212,165,116,.08)' : 'none',
                    transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                  }}>
                  <div className="vereador-card__row">
                    <CandidatoAvatar candidato={v} foto={v.foto} nome={v.nome} size={36} className="rounded-lg" />
                    <div className="vereador-card__info">
                      <p className="vereador-card__name">{capitalize(v.nome)}</p>
                      <p className="vereador-card__party">{v.partido} {age ? `· ${age} anos` : ''}</p>
                    </div>
                  </div>
                  {v.ocupacao && v.ocupacao !== '#NULO' && (
                    <p className="text-[9px] text-gray-500 truncate">{capitalize(v.ocupacao)}</p>
                  )}
                  {v.votos > 0 && (
                    <p className="text-[8px] text-gray-600 mt-0.5">{v.votos.toLocaleString('pt-BR')} votos</p>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Detail panel */}
        <div className="md:w-72 shrink-0">
          <div className="rounded-xl border overflow-hidden sticky top-0"
            style={{
              background: sel ? 'rgba(255,255,255,.03)' : 'rgba(255,255,255,.01)',
              borderColor: sel ? 'rgba(212,165,116,.2)' : 'rgba(255,255,255,.06)',
              minHeight: 280,
            }}>
            {sel ? (
              <div className="p-4 flex flex-col items-center">
                <CandidatoAvatar candidato={sel} foto={sel.foto} nome={sel.nome} size={72} className="rounded-xl border-2 border-amber-500/20 mb-3" />
                <h3 className="text-base font-bold text-white text-center">{capitalize(sel.nome)}</h3>
                <p className="text-xs text-gray-400 mb-3">{sel.partido} · Vereador(a)</p>

                <div className="w-full space-y-2 text-[10px]">
                  {sel.ocupacao && sel.ocupacao !== '#NULO' && (
                    <div className="flex items-center justify-between text-gray-300">
                      <span className="text-gray-500">Profissão</span>
                      <span className="text-right">{capitalize(sel.ocupacao)}</span>
                    </div>
                  )}
                  {sel.grauInstrucao && sel.grauInstrucao !== '#NULO' && (
                    <div className="flex items-center justify-between text-gray-300">
                      <span className="text-gray-500">Escolaridade</span>
                      <span className="text-right">{capitalize(sel.grauInstrucao)}</span>
                    </div>
                  )}
                  {calcAge(sel.dataNascimento) && (
                    <div className="flex items-center justify-between text-gray-300">
                      <span className="text-gray-500">Idade</span>
                      <span>{calcAge(sel.dataNascimento)} anos</span>
                    </div>
                  )}
                  {sel.genero && (
                    <div className="flex items-center justify-between text-gray-300">
                      <span className="text-gray-500">Gênero</span>
                      <span>{capitalize(sel.genero)}</span>
                    </div>
                  )}
                </div>

                {/* Politician indicators */}
                {sel?.indicadores && (
                  <div className="w-full mt-4 pt-3 border-t border-white/[.06]">
                    <p className="text-[9px] text-gray-500 uppercase tracking-wider mb-2 text-center">Indicadores do Político</p>
                    <div className="space-y-1.5">
                      {[
                        { icon: <Sparkles size={10} />, label: 'Carisma', value: sel.indicadores.carisma, color: 'bg-green-400', textColor: 'text-green-400' },
                        { icon: <Shield size={10} />, label: 'Influência', value: sel.indicadores.influencia, color: 'bg-purple-400', textColor: 'text-purple-400' },
                        { icon: <DollarSign size={10} />, label: 'Recursos', value: sel.indicadores.recursos, color: 'bg-amber-400', textColor: 'text-amber-400' },
                        { icon: <AlertTriangle size={10} />, label: 'Risco', value: sel.indicadores.risco, color: 'bg-red-400', textColor: 'text-red-400' },
                      ].map(s => (
                        <div key={s.label} className="flex items-center gap-1.5">
                          <span className="text-gray-500 shrink-0">{s.icon}</span>
                          <span className="text-[9px] text-gray-500 w-12 shrink-0">{s.label}</span>
                          <div className="flex-1 h-1.5 rounded-full bg-white/[.06] overflow-hidden">
                            <div className={`h-full rounded-full ${s.color} transition-all duration-500`} style={{ width: `${s.value}%` }} />
                          </div>
                          <span className={`text-[10px] font-bold w-6 text-right shrink-0 ${s.textColor}`}>{Math.round(s.value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Dynamic bonus preview */}
                {(() => {
                  const ind = sel?.indicadores || {};
                  const bonus = (v) => Math.max(1, Math.round((v || 0) / 10));
                  const carVal = bonus(ind.carisma);
                  const infVal = bonus(ind.influencia);
                  const monVal = bonus(ind.recursos);
                  const riskVal = bonus(ind.risco);
                  const totalVer = vereadores.length || 13;
                  const vPos = sel?.position || totalVer;
                  const partyVer = vereadores.filter(e => e.partido === sel?.partido)?.length || 1;
                  return (
                    <div className="w-full mt-4 pt-3 border-t border-white/[.06]">
                      <p className="text-[9px] text-gray-500 uppercase tracking-wider mb-2 text-center">Efeitos como assessor</p>
                      <div className="grid grid-cols-3 gap-1.5 text-center">
                        <div className="p-2 rounded-lg bg-green-500/10">
                          <Users size={12} className="text-green-400 mx-auto mb-0.5" />
                          <p className="text-[9px] text-green-400 font-bold">+{carVal}</p>
                          <p className="text-[8px] text-gray-500">Carisma</p>
                        </div>
                        <div className="p-2 rounded-lg bg-purple-500/10">
                          <Handshake size={12} className="text-purple-400 mx-auto mb-0.5" />
                          <p className="text-[9px] text-purple-400 font-bold">+{infVal}</p>
                          <p className="text-[8px] text-gray-500">Influência</p>
                        </div>
                        <div className="p-2 rounded-lg bg-amber-500/10">
                          <Wallet size={12} className="text-amber-400 mx-auto mb-0.5" />
                          <p className="text-[9px] text-amber-400 font-bold">+{monVal}</p>
                          <p className="text-[8px] text-gray-500">Recursos</p>
                        </div>
                      </div>
                      {riskVal > 0 && (
                        <div className="mt-2 p-2 rounded-lg border border-red-500/20 bg-red-500/5 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <AlertTriangle size={11} className="text-red-400" />
                            <p className="text-[9px] text-red-400 font-bold">+{riskVal} Risco herdado</p>
                          </div>
                          <p className="text-[7px] text-red-400/60 mt-0.5">Vereadores controversos trazem riscos ao assessor</p>
                        </div>
                      )}
                      {sel?.votos > 0 && (
                        <p className="text-[8px] text-gray-500 text-center mt-2">
                          {sel.votos.toLocaleString('pt-BR')} votos · {vPos}º mais votado · {partyVer} do {sel.partido} na câmara
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500 text-xs px-4 text-center">
                Selecione um vereador para ver seus detalhes e iniciar como assessor
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-white/[.06] flex items-center justify-between gap-2">
        <button onClick={onBack}
          className="flex items-center gap-1 sm:gap-1.5 px-3 sm:px-4 py-2 rounded-lg text-[10px] sm:text-xs font-medium border border-white/10 text-gray-300 hover:text-white hover:border-white/20 transition-colors shrink-0">
          <ArrowLeft size={14} /> <span className="hidden sm:inline">Voltar</span>
        </button>
        <button
          disabled={selected === null}
          onClick={() => onSelect(vereadores[selected])}
          className="flex items-center gap-1.5 px-4 sm:px-6 py-2.5 rounded-lg text-xs sm:text-sm font-bold transition-all flex-1 sm:flex-none justify-center"
          style={{
            background: selected !== null ? 'rgba(212,165,116,.95)' : 'rgba(255,255,255,.08)',
            color: selected !== null ? '#111' : 'rgba(255,255,255,.35)',
            cursor: selected !== null ? 'pointer' : 'not-allowed',
          }}>
          <Zap size={14} /> {sel ? `Assessorar ${capitalize(sel.nome).split(' ').slice(0, 2).join(' ')}` : 'Escolher Vereador'}
        </button>
      </div>
    </div>
  );
}
